/**
 * PROTOTIPO staff-only "de la HP a la geometría" — CATÁLOGO ÚNICO DE PARÁMETROS
 * para enriquecer las FOTOS (Gemini) y la ANIMACIÓN (Veo).
 *
 * ⭐ Este archivo es el ÚNICO lugar donde se definen los parámetros creativos.
 * Para agregar/quitar un control del modal o afinar un texto de prompt, se toca
 * SOLO aquí: el modal los pinta de forma genérica, el worker resuelve los
 * fragmentos y el pipeline Python los concatena. No hay tablas de dominio (esto
 * es texto creativo de prompt, no aliases/unidades/materiales), así que ampliarlo
 * NO cae en la regla de "prohibido hardcode".
 *
 * ACOTADO a obra civil de EDIFICACIÓN (no infraestructura). La lista está pensada
 * con ojo de ARQUITECTO: la MATERIALIDAD y el partido de diseño pesan más que el
 * dato técnico.
 *
 * Dos familias:
 *  1) PARÁMETROS INFERIDOS de la HP → viven en la `ficha` (ver GEOMETRIA_FICHA_CAMPOS);
 *     la IA los deduce de los conceptos y el usuario los puede sobreescribir.
 *  2) PARÁMETROS MANUALES → el usuario los elige en el modal (ver GEOMETRIA_PARAMETROS);
 *     cada opción trae su fragmento de prompt (en inglés, que es como responde mejor
 *     el generador de imagen) que se inyecta verbatim.
 */
import type { GeometriaFicha } from './domain.js';

/** Dónde pega un parámetro: en la FOTO (y por herencia en el fotograma base del
 * video) o SOLO en el clip de animación. */
export type GeometriaAmbito = 'foto' | 'video';

export interface GeometriaParamOpcion {
  id: string;
  label: string;
  /** Fragmento de prompt (inglés) que se inyecta al elegir esta opción. '' = ninguno. */
  prompt: string;
}

export interface GeometriaParam {
  id: string;
  label: string;
  /** Ayuda breve opcional para el usuario (español). */
  ayuda?: string;
  ambito: GeometriaAmbito;
  /** true = selección múltiple. En `parametros` el valor va como ids separados por coma. */
  multi?: boolean;
  opciones: readonly GeometriaParamOpcion[];
  /** id (o ids coma-separados si `multi`) de la opción por defecto. */
  predeterminado: string;
}

/**
 * PARÁMETROS MANUALES. El modal los renderiza de forma genérica (los de ámbito
 * 'foto' en la sección de la foto; los de 'video' junto a los controles de la
 * animación). Agregar uno nuevo aquí lo hace aparecer solo en el modal y en el prompt.
 */
export const GEOMETRIA_PARAMETROS: readonly GeometriaParam[] = [
  // ---- Composición y vanos ----
  {
    id: 'lleno_vacio',
    label: 'Relación lleno-vacío',
    ambito: 'foto',
    predeterminado: 'equilibrado',
    opciones: [
      { id: 'macizo', label: 'Predomina el muro', prompt: 'Facade composition: solid and massive, small openings, wall dominates over glazing.' },
      { id: 'equilibrado', label: 'Equilibrado', prompt: 'Facade composition: balanced solid-to-void ratio between walls and openings.' },
      { id: 'acristalado', label: 'Muy acristalado', prompt: 'Facade composition: highly glazed, large window surfaces, light and transparent.' },
    ],
  },
  {
    id: 'ventaneria',
    label: 'Ventanería',
    ambito: 'foto',
    predeterminado: 'aluminio',
    opciones: [
      { id: 'aluminio', label: 'Aluminio', prompt: 'Window frames: modern aluminum profiles.' },
      { id: 'herreria', label: 'Herrería', prompt: 'Window frames: black steel / wrought-iron (herrería) frames.' },
      { id: 'pvc', label: 'PVC', prompt: 'Window frames: white PVC profiles.' },
      { id: 'sin', label: 'Sin preferencia', prompt: '' },
    ],
  },
  {
    id: 'tipo_ventana',
    label: 'Tipo de ventana',
    ambito: 'foto',
    predeterminado: 'antepecho',
    opciones: [
      { id: 'antepecho', label: 'Ventana con antepecho', prompt: 'Windows: normal punched windows with a sill (not floor-to-ceiling).' },
      { id: 'pisotecho', label: 'Ventanal piso-techo', prompt: 'Windows: full-height floor-to-ceiling glazing on the main facade.' },
      { id: 'sin', label: 'Sin preferencia', prompt: '' },
    ],
  },
  {
    id: 'elementos',
    label: 'Elementos arquitectónicos',
    ayuda: 'Rasgos que dan personalidad. Puedes elegir varios.',
    ambito: 'foto',
    multi: true,
    predeterminado: '',
    opciones: [
      { id: 'portico', label: 'Pórtico de acceso', prompt: 'Feature: a covered entrance portico marking the access.' },
      { id: 'volado', label: 'Volado', prompt: 'Feature: a bold cantilevered volume (volado) projecting over the entrance.' },
      { id: 'doble_altura', label: 'Doble altura', prompt: 'Feature: a double-height space visible on the facade.' },
      { id: 'pergola', label: 'Pérgola', prompt: 'Feature: a wooden or steel pergola.' },
      { id: 'celosia', label: 'Celosía / parasol', prompt: 'Feature: a decorative lattice / brise-soleil (celosía) sun screen on the facade.' },
      { id: 'balcon', label: 'Balcón / terraza', prompt: 'Feature: a balcony or terrace with railing.' },
      { id: 'patio', label: 'Patio', prompt: 'Feature: an interior patio glimpsed through the composition.' },
    ],
  },
  // ---- Emplazamiento ----
  {
    id: 'emplazamiento',
    label: 'Emplazamiento',
    ambito: 'foto',
    predeterminado: 'suburbano',
    opciones: [
      { id: 'urbano', label: 'Urbano', prompt: 'Setting: dense urban context, paved street, neighboring buildings close by.' },
      { id: 'suburbano', label: 'Suburbano', prompt: 'Setting: quiet suburban residential street with sidewalks and low neighboring houses.' },
      { id: 'rural', label: 'Rural', prompt: 'Setting: rural countryside, open natural surroundings, simple road, distant vegetation.' },
      { id: 'costero', label: 'Costero', prompt: 'Setting: coastal location, palms and sandy tones, bright sky, hint of the sea in the distance.' },
    ],
  },
  {
    id: 'lote',
    label: 'Ubicación en el lote',
    ambito: 'foto',
    predeterminado: 'intermedio',
    opciones: [
      { id: 'intermedio', label: 'Lote intermedio', prompt: 'Plot: mid-block lot, one main facade to the street.' },
      { id: 'esquina', label: 'Lote esquina', prompt: 'Plot: corner lot, two facades exposed to the streets.' },
    ],
  },
  {
    id: 'limite',
    label: 'Límite del predio',
    ambito: 'foto',
    predeterminado: 'sin',
    opciones: [
      { id: 'sin', label: 'Sin preferencia', prompt: '' },
      { id: 'barda', label: 'Barda', prompt: 'Property edge: a solid perimeter wall (barda) at the front.' },
      { id: 'reja', label: 'Reja', prompt: 'Property edge: a metal fence / railing (reja) letting the facade be seen.' },
      { id: 'abierto', label: 'Sin barda', prompt: 'Property edge: open front, no perimeter wall or fence.' },
    ],
  },
  // ---- Paisajismo ----
  {
    id: 'paisajismo',
    label: 'Paisajismo',
    ayuda: 'Puedes elegir varios (o ninguno).',
    ambito: 'foto',
    multi: true,
    predeterminado: 'jardin,arbolado,andadores',
    opciones: [
      { id: 'jardin', label: 'Jardín', prompt: 'Landscaping: green lawn and planted front garden.' },
      { id: 'arbolado', label: 'Arbolado', prompt: 'Landscaping: several leafy trees of different sizes.' },
      { id: 'espejo_agua', label: 'Espejo de agua', prompt: 'Landscaping: a reflecting water feature.' },
      { id: 'andadores', label: 'Andadores', prompt: 'Landscaping: paved walkways from the sidewalk to the entrance.' },
      { id: 'estacionamiento', label: 'Estacionamiento', prompt: 'Landscaping: an on-site parking area / carport.' },
    ],
  },
  // ---- Atmósfera ----
  {
    id: 'hora',
    label: 'Hora del día',
    ambito: 'foto',
    predeterminado: 'mediodia',
    opciones: [
      { id: 'manana', label: 'Mañana', prompt: 'Time of day: early morning, soft cool light, gentle long shadows.' },
      { id: 'mediodia', label: 'Mediodía', prompt: 'Time of day: bright sunny midday, warm natural daylight, soft shadows.' },
      { id: 'atardecer', label: 'Atardecer', prompt: 'Time of day: golden hour at sunset, warm low sun, glowing light.' },
      { id: 'noche', label: 'Noche', prompt: 'Time of day: night scene, warm facade and interior lights glowing, deep blue twilight sky.' },
    ],
  },
  {
    id: 'cielo',
    label: 'Cielo / clima',
    ambito: 'foto',
    predeterminado: 'despejado',
    opciones: [
      { id: 'despejado', label: 'Despejado', prompt: 'Sky: clear blue sky with a few soft clouds.' },
      { id: 'dramatico', label: 'Nublado dramático', prompt: 'Sky: dramatic overcast sky with textured clouds and moody diffuse light.' },
    ],
  },
  // ---- Encuadre y escala ----
  {
    id: 'encuadre',
    label: 'Encuadre arquitectónico',
    ambito: 'foto',
    predeterminado: 'peaton',
    opciones: [
      { id: 'peaton', label: 'A nivel de peatón', prompt: 'Camera: eye-level three-quarter view from the sidewalk, natural pedestrian perspective.' },
      { id: 'acceso', label: 'Aproximación al acceso', prompt: 'Camera: approaching the main entrance, framing the access as the focal point.' },
      { id: 'aereo', label: '3/4 aéreo (dron)', prompt: 'Camera: elevated three-quarter aerial / drone view looking slightly down, showing two facades and the roof.' },
      { id: 'contrapicado', label: 'Contrapicado heroico', prompt: 'Camera: low heroic upward angle emphasizing the building height and presence.' },
    ],
  },
  {
    id: 'entourage',
    label: 'Escala (entourage)',
    ayuda: 'Elementos para dar sensación de escala. Puedes elegir varios.',
    ambito: 'foto',
    multi: true,
    predeterminado: 'personas,autos',
    opciones: [
      { id: 'personas', label: 'Personas', prompt: 'Entourage: one or two people for scale.' },
      { id: 'autos', label: 'Autos', prompt: 'Entourage: a parked car at the curb.' },
      { id: 'mobiliario', label: 'Mobiliario urbano', prompt: 'Entourage: street furniture (a bench, a lamppost) for context.' },
    ],
  },
  // ---- Video ----
  {
    id: 'movimiento',
    label: 'Movimiento de cámara',
    ambito: 'video',
    predeterminado: 'orbita',
    opciones: [
      { id: 'orbita', label: 'Órbita', prompt: 'Camera motion: slow smooth orbit around the building, revealing two facades.' },
      { id: 'aproximacion', label: 'Aproximación al acceso', prompt: 'Camera motion: slow steady push-in approaching the main entrance.' },
      { id: 'paneo', label: 'Paneo lateral', prompt: 'Camera motion: smooth lateral tracking shot gliding along the facade.' },
      { id: 'ascenso', label: 'Ascenso que revela la volumetría', prompt: 'Camera motion: slow crane ascent that rises to reveal the full volume and roof.' },
    ],
  },
] as const;

/** Duraciones de video que acepta Veo 3.1 (segundos). El costo se muestra en el modal. */
export const GEOMETRIA_VIDEO_DURACIONES = [4, 6, 8] as const;

/** Niveles de calidad/costo del video. El id se envía al worker; el precio/seg y el
 * modelo real viven en configuración (env) del worker. Default del modal = Fast. */
export const GEOMETRIA_VIDEO_CALIDADES = [
  { id: 'fast', label: 'Alta (Veo Fast)' },
  { id: 'lite', label: 'Económica (Veo Lite)' },
] as const;

export type GeometriaVideoCalidad = (typeof GEOMETRIA_VIDEO_CALIDADES)[number]['id'];

/**
 * PARÁMETROS INFERIDOS de la HP (la "ficha"). Descriptor para que el modal pinte
 * los campos editables de forma genérica y prellenados con lo que dedujo la IA.
 * Todos texto libre editable. Agregar un campo aquí (y en GeometriaFicha +
 * fichaRender) lo hace aparecer editable. La MATERIALIDAD de fachada es el driver
 * principal del render.
 */
export interface GeometriaFichaCampo {
  id: keyof GeometriaFicha;
  label: string;
  placeholder?: string;
}

export const GEOMETRIA_FICHA_CAMPOS: readonly GeometriaFichaCampo[] = [
  { id: 'tipologia', label: 'Tipología', placeholder: 'ej. vivienda unifamiliar / oficinas / comercial' },
  { id: 'niveles', label: 'Niveles', placeholder: 'ej. 1 nivel / 2 niveles' },
  { id: 'volumetria', label: 'Volumetría', placeholder: 'ej. doble altura, voladizo, retranqueos' },
  { id: 'fachada_material', label: 'Fachada · material dominante', placeholder: 'ej. tabique aparente / aplanado y pintura / concreto aparente' },
  { id: 'fachada_acento', label: 'Fachada · material de acento', placeholder: 'ej. cantera, madera, celosía' },
  { id: 'fachada_textura', label: 'Fachada · textura / acabado', placeholder: 'ej. liso mate, martelinado, aparente' },
  { id: 'cubierta', label: 'Cubierta', placeholder: 'ej. losa plana / azotea verde / inclinada / bóveda' },
] as const;

/** Fragmentos de prompt resueltos a partir de la selección manual del usuario. */
export interface GeometriaFragmentos {
  /** Para la FOTO (Gemini) — la toma "hero" con el encuadre elegido por el usuario. */
  foto: string[];
  /** Para la ANIMACIÓN (Veo) — el movimiento de cámara. */
  video: string[];
  /**
   * Para la 2ª imagen SEMILLA del video (Gemini): idéntica a `foto` (mismo edificio,
   * misma materialidad, misma escena) SALVO el encuadre, que se cambia a un ángulo
   * complementario. Así la FOTO y el VIDEO son DOS TOMAS distintas del mismo edificio
   * en vez de que el video parezca "un cuadro de la foto". Vacío si no se pidió video.
   */
  videoSeedFoto: string[];
}

/**
 * Encuadre de la SEMILLA del video: un ángulo COMPLEMENTARIO al de la foto para que
 * las dos piezas de venta se vean claramente distintas. Se prefiere el 3/4 aéreo
 * (dron), que revela dos fachadas y el techo y sirve para cualquier movimiento de
 * cámara; si la foto YA usa ese encuadre, la semilla cae al nivel de peatón. Es una
 * elección creativa entre opciones ya definidas arriba (no una tabla de dominio),
 * exenta de la regla "prohibido hardcode" (ver encabezado de este archivo).
 */
const ENCUADRE_VIDEO_PREFERIDO = 'aereo';
const ENCUADRE_VIDEO_ALTERNATIVO = 'peaton';

/**
 * Traduce la selección del usuario (`parametros`: id de parámetro → id de opción,
 * o ids coma-separados si el parámetro es `multi`) a los fragmentos de prompt. Si
 * un parámetro no viene, usa su opción por defecto. Lo usa el worker (foto →
 * pipeline Python, video → Veo). ÚNICO punto de armado.
 */
export function resolverGeometriaFragmentos(
  parametros: Record<string, string> | undefined,
): GeometriaFragmentos {
  const foto: string[] = [];
  const video: string[] = [];
  const videoSeedFoto: string[] = [];
  const destino = (a: GeometriaAmbito) => (a === 'video' ? video : foto);

  // Encuadre elegido para la foto → la semilla del video toma OTRO ángulo.
  const encuadreParam = GEOMETRIA_PARAMETROS.find((p) => p.id === 'encuadre');
  const encuadreFoto = (parametros?.['encuadre'] ?? encuadreParam?.predeterminado ?? '').trim();
  const encuadreVideo =
    encuadreFoto === ENCUADRE_VIDEO_PREFERIDO ? ENCUADRE_VIDEO_ALTERNATIVO : ENCUADRE_VIDEO_PREFERIDO;

  for (const p of GEOMETRIA_PARAMETROS) {
    const crudo = parametros?.[p.id] ?? p.predeterminado;
    const ids = p.multi
      ? crudo.split(',').map((s) => s.trim()).filter(Boolean)
      : [crudo.trim()].filter(Boolean);
    for (const id of ids) {
      const op = p.opciones.find((o) => o.id === id);
      if (op?.prompt) destino(p.ambito).push(op.prompt);
    }
    // La semilla del video hereda TODO lo de la foto salvo el encuadre (otro ángulo).
    if (p.ambito === 'foto') {
      if (p.id === 'encuadre') {
        const opV = p.opciones.find((o) => o.id === encuadreVideo);
        if (opV?.prompt) videoSeedFoto.push(opV.prompt);
      } else {
        for (const id of ids) {
          const op = p.opciones.find((o) => o.id === id);
          if (op?.prompt) videoSeedFoto.push(op.prompt);
        }
      }
    }
  }
  return { foto, video, videoSeedFoto };
}
